import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import SnomedSearchField from "./SnomedSearchField";
import {
  fetchFeasibilityDetail,
  fetchFeasibilityList,
  fetchFeasibilityVolunteers,
  saveFeasibility,
} from "./participantService";
import FeasibilityVolunteerDetailModal from "./FeasibilityVolunteerDetailModal";
import {
  getParticipantAgeYears,
  getParticipantProfilePictureUrl,
} from "./participantReportUtils";
import "./participantmanager.scss";

const EMPTY_FORM = {
  ParticipantName: "",
  MinAgelimit: "",
  MaxAgeLimit: "",
  MinBMIValue: "",
  BMIValue: "",
  InclFilter: "All",
  ExclFilter: "All",
  InclFiltermedi: "All",
  ExclFiltermedi: "All",
};

const GENDER_CHOICES = ["Male", "Female", "Other"];
const FILTER_MODES = ["All", "Any"];

const VOLUNTEER_COLUMNS = [
  { key: "VolunteerName", label: "Participant Name" },
  { key: "VolunteerNo", label: "Participant ID" },
  { key: "DOB", label: "Date of Birth" },
  { key: "Gender", label: "Gender" },
  { key: "ContactNumber", label: "Mobile Number" },
  { key: "EmailId", label: "Email Address" },
];

function termKey(item) {
  return `${String(item?.code || "").toLowerCase()}|${String(item?.term || "").toLowerCase()}|${String(item?.termType || "").toLowerCase()}`;
}

function postCodeLabel(row) {
  return String(
    row?.PostCode ??
      row?.postCode ??
      row?.Postcode ??
      row?.Value ??
      row?.value ??
      row?.Name ??
      row?.name ??
      "",
  ).trim();
}

function fieldOf(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function volunteerCardId(row, index) {
  return (
    fieldOf(
      row,
      "VolunteerDemRecordId",
      "VolunteerId",
      "VolunteerNo",
      "volunteerDemRecordId",
    ) || `vol-${index}`
  );
}

/** SP stores 0 for "not set" — show empty in the form. */
function blankZero(value) {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (Number.isFinite(n) && n === 0) return "";
  return String(value);
}

function splitCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapMedInfoToBuckets(medInfo = []) {
  const medInclude = [];
  const medExclude = [];
  const mediInclude = [];
  const mediExclude = [];

  (Array.isArray(medInfo) ? medInfo : []).forEach((row) => {
    const term = String(row?.SnomedTerm ?? row?.snomedTerm ?? "").trim();
    if (!term) return;
    const item = {
      term,
      code: String(row?.SnomedCode ?? row?.snomedCode ?? "").trim(),
      termType: String(
        row?.Clinicalsection ??
          row?.ClinicalSection ??
          row?.clinicalsection ??
          "",
      ).trim(),
    };
    const section = item.termType.toUpperCase();
    const inEx = String(
      row?.IncludeOrExclude ?? row?.InOrExclution ?? "",
    )
      .trim()
      .toLowerCase();
    const isMedication = section === "MEDICATION";

    if (inEx === "include") {
      if (isMedication) mediInclude.push(item);
      else medInclude.push(item);
    } else if (inEx === "exclude") {
      if (isMedication) mediExclude.push(item);
      else medExclude.push(item);
    }
  });

  return { medInclude, medExclude, mediInclude, mediExclude };
}

function isUsableProfilePictureUrl(url) {
  const value = String(url ?? "").trim();
  if (!value) return false;
  const lower = value.toLowerCase();
  if (lower.includes("avatar.png") || lower.includes("/images/avatar")) {
    return false;
  }
  return true;
}

function ParticipantResultCard({ row, onOpen }) {
  const name = fieldOf(row, "VolunteerName", "volunteerName") || "—";
  const id = fieldOf(row, "VolunteerNo", "VolunteerId", "volunteerNo");
  const gender = fieldOf(row, "Gender", "gender");
  const mobile = fieldOf(row, "ContactNumber", "MobileNumber", "contactNumber");
  const email = fieldOf(row, "EmailId", "Email", "emailId");
  const ageYears = getParticipantAgeYears(fieldOf(row, "DOB", "Dob", "dob"));
  const ageLabel = ageYears == null ? "" : `${ageYears} years`;
  const photoUrl = getParticipantProfilePictureUrl(row);
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = isUsableProfilePictureUrl(photoUrl) && !imgFailed;
  const volKey = fieldOf(
    row,
    "VolunteerDemRecordId",
    "VolunteerId",
    "volunteerDemRecordId",
  );

  const metaParts = [ageLabel, gender, mobile, email].filter(Boolean);

  return (
    <button
      type="button"
      className="pm-feas-vol-card"
      onClick={() => onOpen?.(row)}
      disabled={!volKey}
      title={volKey ? "View participant details" : undefined}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt=""
          className="pm-feas-vol-card__avatar pm-feas-vol-card__avatar--photo"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          className="pm-feas-vol-card__avatar pm-feas-vol-card__avatar--icon"
          aria-hidden="true"
        >
          <i className="fas fa-user" />
        </span>
      )}
      <div className="pm-feas-vol-card__body">
        <div className="pm-feas-vol-card__row pm-feas-vol-card__row--primary">
          <div className="pm-feas-vol-card__title">
            {id ? (
              <span className="pm-feas-vol-card__badge" title={id}>
                {id}
              </span>
            ) : null}
            <span className="pm-feas-vol-card__name" title={name}>
              {name}
            </span>
          </div>
          <i
            className="fas fa-chevron-right pm-feas-vol-card__chevron"
            aria-hidden="true"
          />
        </div>
        {metaParts.length ? (
          <div
            className="pm-feas-vol-card__row pm-feas-vol-card__row--meta"
            title={metaParts.join(" · ")}
          >
            {metaParts.map((part, index) => (
              <React.Fragment key={`${part}-${index}`}>
                {index > 0 ? (
                  <span className="pm-feas-vol-card__dot" aria-hidden="true">
                    ·
                  </span>
                ) : null}
                <span className="pm-feas-vol-card__meta-item">{part}</span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>
    </button>
  );
}

function validateRanges(form) {
  const errors = {};
  const minAge = String(form.MinAgelimit || "").trim();
  const maxAge = String(form.MaxAgeLimit || "").trim();
  const minBmi = String(form.MinBMIValue || "").trim();
  const maxBmi = String(form.BMIValue || "").trim();

  if (minAge && !/^\d{1,2}$/.test(minAge)) {
    errors.MinAgelimit = "Enter a valid min age.";
  }
  if (maxAge && !/^\d{1,3}$/.test(maxAge)) {
    errors.MaxAgeLimit = "Enter a valid max age.";
  }
  if (minAge && maxAge && Number(minAge) >= Number(maxAge)) {
    errors.MaxAgeLimit = "Min age must be less than max age.";
  }
  if (minBmi && Number.isNaN(Number(minBmi))) {
    errors.MinBMIValue = "Enter a valid min BMI.";
  }
  if (maxBmi && Number.isNaN(Number(maxBmi))) {
    errors.BMIValue = "Enter a valid max BMI.";
  }
  if (minBmi && maxBmi && Number(minBmi) >= Number(maxBmi)) {
    errors.BMIValue = "Min BMI must be less than max BMI.";
  }
  return errors;
}

function csvEscape(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function exportVolunteersCsv(rows) {
  const headers = VOLUNTEER_COLUMNS.map((c) => c.label);
  const lines = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) =>
      VOLUNTEER_COLUMNS.map((col) => csvEscape(row?.[col.key])).join(","),
    ),
  ];
  const blob = new Blob([lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Feasibility-Participant.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function ModeToggle({ name, value, onChange }) {
  return (
    <div className="pm-feas-mode" role="radiogroup" aria-label={name}>
      {FILTER_MODES.map((mode) => (
        <label key={mode} className="pm-feas-mode__option">
          <input
            type="radio"
            name={name}
            value={mode}
            checked={value === mode}
            onChange={() => onChange(mode)}
          />
          <span>{mode}</span>
        </label>
      ))}
    </div>
  );
}

function TermChips({ items, onRemove, emptyLabel }) {
  if (!items.length) {
    return <p className="pm-feas-empty">{emptyLabel}</p>;
  }

  const groups = items.reduce((acc, item) => {
    const section = item.termType || "Term";
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <div className="pm-feas-terms">
      {Object.entries(groups).map(([section, list]) => (
        <div key={section} className="pm-feas-terms__group">
          <div className="pm-feas-terms__section">{section}</div>
          <ul className="pm-feas-terms__list">
            {list.map((item) => (
              <li key={termKey(item)} className="pm-feas-tag">
                <span title={item.term}>{item.term}</span>
                <button
                  type="button"
                  aria-label={`Remove ${item.term}`}
                  onClick={() => onRemove(item)}
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Field({ label, required, error, hint, children, className = "" }) {
  return (
    <div className={`pm-add-field ${className}`.trim()}>
      {label ? (
        <label className="pm-add-field__label">
          {label}
          {required ? <span className="pm-add-field__req">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <span className="pm-add-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="pm-add-field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

export default function CreateFeasibilityModal({
  open,
  onClose,
  onSaved,
  participantId = "",
}) {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const isEdit = Boolean(String(participantId || "").trim());

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [genders, setGenders] = useState([]);
  const [postCodes, setPostCodes] = useState([]);
  const [postCodeOptions, setPostCodeOptions] = useState([]);
  const [medInclude, setMedInclude] = useState([]);
  const [medExclude, setMedExclude] = useState([]);
  const [mediInclude, setMediInclude] = useState([]);
  const [mediExclude, setMediExclude] = useState([]);
  const [volunteerRows, setVolunteerRows] = useState([]);
  const [queryRan, setQueryRan] = useState(false);
  const [filterNotice, setFilterNotice] = useState("");
  const [savedParticipantId, setSavedParticipantId] = useState("");
  const [detailVolunteerId, setDetailVolunteerId] = useState("");
  const resultsRef = useRef(null);

  const resetState = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setGenders([]);
    setPostCodes([]);
    setMedInclude([]);
    setMedExclude([]);
    setMediInclude([]);
    setMediExclude([]);
    setVolunteerRows([]);
    setQueryRan(false);
    setFilterNotice("");
    setSavedParticipantId("");
    setDetailVolunteerId("");
  }, []);

  const mergePostCodeOptions = useCallback((selected = []) => {
    setPostCodeOptions((prev) => {
      const map = new Map(
        (prev || []).map((option) => [option.value, option]),
      );
      selected.forEach((value) => {
        const key = String(value || "").trim();
        if (key && !map.has(key)) {
          map.set(key, { value: key, label: key });
        }
      });
      return [...map.values()];
    });
  }, []);

  const loadLookups = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    try {
      const { postCodes: list } = await fetchFeasibilityList(userData);
      const options = (Array.isArray(list) ? list : [])
        .map(postCodeLabel)
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index)
        .map((value) => ({ value, label: value }));
      setPostCodeOptions(options);
    } catch {
      setPostCodeOptions([]);
    }
  }, [userData]);

  const bindDetailToForm = useCallback(
    (id, participant, medInfo) => {
      const nextForm = {
        ParticipantName: String(
          participant?.ParticipantName ?? participant?.participantName ?? "",
        ).trim(),
        MinAgelimit: blankZero(
          participant?.MinAgelimit ?? participant?.minAgelimit,
        ),
        MaxAgeLimit: blankZero(
          participant?.MaxAgeLimit ?? participant?.maxAgeLimit,
        ),
        MinBMIValue: blankZero(
          participant?.MinBMIValue ?? participant?.minBMIValue,
        ),
        BMIValue: blankZero(participant?.BMIValue ?? participant?.bmiValue),
        InclFilter: participant?.InclFilter || "All",
        ExclFilter: participant?.ExclFilter || "All",
        InclFiltermedi:
          participant?.InclMedicationFilter ||
          participant?.InclFiltermedi ||
          "All",
        ExclFiltermedi:
          participant?.ExclMedicationFilter ||
          participant?.ExclFiltermedi ||
          "All",
      };
      const nextGenders = splitCsv(participant?.Gender ?? participant?.gender);
      const nextPostCodes = splitCsv(
        participant?.PostCode ?? participant?.postCode,
      );
      const buckets = mapMedInfoToBuckets(medInfo);
      setForm(nextForm);
      setGenders(nextGenders);
      setPostCodes(nextPostCodes);
      setMedInclude(buckets.medInclude);
      setMedExclude(buckets.medExclude);
      setMediInclude(buckets.mediInclude);
      setMediExclude(buckets.mediExclude);
      setSavedParticipantId(String(id).trim());
      mergePostCodeOptions(nextPostCodes);
      return {
        form: nextForm,
        genders: nextGenders,
        postCodes: nextPostCodes,
        medInclude: buckets.medInclude,
        medExclude: buckets.medExclude,
        mediInclude: buckets.mediInclude,
        mediExclude: buckets.mediExclude,
        participantId: String(id).trim(),
      };
    },
    [mergePostCodeOptions],
  );

  const buildPayloadFromBound = useCallback((bound) => {
    const toTerm = (item, inOrEx) => ({
      SnomedTerm: item.term,
      SnomedCode: item.code || "",
      ClinicalSection: item.termType || "",
      InOrExclution: inOrEx,
    });
    return {
      ParticipantId: bound.participantId || "",
      ParticipantName: String(bound.form?.ParticipantName || "").trim(),
      Gender: (bound.genders || []).join(","),
      MinAgelimit: bound.form?.MinAgelimit
        ? Number(bound.form.MinAgelimit)
        : 0,
      MaxAgeLimit: bound.form?.MaxAgeLimit
        ? Number(bound.form.MaxAgeLimit)
        : 0,
      MinBMIValue: String(bound.form?.MinBMIValue || "").trim(),
      BMIValue: String(bound.form?.BMIValue || "").trim(),
      InclFilter: bound.form?.InclFilter || "All",
      ExclFilter: bound.form?.ExclFilter || "All",
      InclFiltermedi: bound.form?.InclFiltermedi || "All",
      ExclFiltermedi: bound.form?.ExclFiltermedi || "All",
      PostCode: (bound.postCodes || []).join(","),
      MedTerms: [
        ...(bound.medInclude || []).map((item) => toTerm(item, "Include")),
        ...(bound.medExclude || []).map((item) => toTerm(item, "Exclude")),
        ...(bound.mediInclude || []).map((item) => toTerm(item, "Include")),
        ...(bound.mediExclude || []).map((item) => toTerm(item, "Exclude")),
      ],
    };
  }, []);

  const runQueryWithPayload = useCallback(
    async (payload, { keepPrevious = false, previousRows = [] } = {}) => {
      let rows = await fetchFeasibilityVolunteers(userData, payload);
      let notice = "";
      const selectedPostCodes = splitCsv(payload.PostCode);
      const selectedGenders = splitCsv(payload.Gender);

      if (!rows?.length && selectedPostCodes.length) {
        rows = await fetchFeasibilityVolunteers(userData, {
          ...payload,
          PostCode: "",
        });
        if (rows?.length) {
          notice =
            selectedPostCodes.length === 1
              ? "No match for selected post code. Showing other filter results."
              : `No match for ${selectedPostCodes.length} selected post codes. Showing other filter results.`;
        }
      }

      if (!rows?.length && selectedGenders.length) {
        const withoutGender = await fetchFeasibilityVolunteers(userData, {
          ...payload,
          PostCode: selectedPostCodes.length
            ? selectedPostCodes.join(",")
            : "",
          Gender: "",
        });
        if (withoutGender?.length) {
          rows = withoutGender;
          notice =
            "No match for selected gender. Showing other filter results.";
        }
      }

      if (!rows?.length && keepPrevious && previousRows.length) {
        setFilterNotice(
          "No match for current filters. Keeping previous results.",
        );
        setQueryRan(true);
        return previousRows;
      }

      const nextRows = Array.isArray(rows) ? rows : [];
      setVolunteerRows(nextRows);
      setFilterNotice(notice);
      setQueryRan(true);
      return nextRows;
    },
    [userData],
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    (async () => {
      resetState();
      const id = String(participantId || "").trim();
      // Postcode list can be slow on live DB — never block form bind on it.
      const lookupsPromise = loadLookups();

      if (!id) {
        await lookupsPromise;
        return;
      }

      showLoader("Loading feasibility query...");
      try {
        const { participant, medInfo } = await fetchFeasibilityDetail(
          userData,
          id,
        );
        if (cancelled) return;
        if (!participant) {
          showAlert({ message: "Feasibility query not found." });
          return;
        }

        const bound = bindDetailToForm(id, participant, medInfo);
        // Bind matched participants into the modal (same as Run Query).
        await runQueryWithPayload(buildPayloadFromBound(bound));
        if (cancelled) return;
        await lookupsPromise;
        if (!cancelled) mergePostCodeOptions(bound.postCodes);
      } catch (err) {
        if (!cancelled) {
          showAlert({
            message: err?.message || "Could not load feasibility query.",
          });
        }
      } finally {
        if (!cancelled) hideLoader();
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only re-bind when modal opens or the edit target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open, participantId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleGender = (gender) => {
    setGenders((prev) =>
      prev.includes(gender)
        ? prev.filter((g) => g !== gender)
        : [...prev, gender],
    );
  };

  const addTerm = (listSetter, item, fallbackType) => {
    if (!item?.term) return;
    const nextItem = {
      code: item.code || "",
      term: item.term,
      termType: item.termType || fallbackType,
    };
    listSetter((prev) => {
      if (prev.some((row) => termKey(row) === termKey(nextItem))) return prev;
      return [...prev, nextItem];
    });
  };

  const removeTerm = (listSetter, item) => {
    listSetter((prev) => prev.filter((row) => termKey(row) !== termKey(item)));
  };

  const buildMedTerms = () => {
    const toTerm = (item, inOrEx) => ({
      SnomedTerm: item.term,
      SnomedCode: item.code || "",
      ClinicalSection: item.termType || "",
      InOrExclution: inOrEx,
    });
    return [
      ...medInclude.map((item) => toTerm(item, "Include")),
      ...medExclude.map((item) => toTerm(item, "Exclude")),
      ...mediInclude.map((item) => toTerm(item, "Include")),
      ...mediExclude.map((item) => toTerm(item, "Exclude")),
    ];
  };

  const buildPayload = (asNew = false) => ({
    ParticipantId: asNew ? "" : savedParticipantId || "",
    ParticipantName: String(form.ParticipantName || "").trim(),
    Gender: genders.join(","),
    MinAgelimit: form.MinAgelimit ? Number(form.MinAgelimit) : 0,
    MaxAgeLimit: form.MaxAgeLimit ? Number(form.MaxAgeLimit) : 0,
    // Empty → "0" in normalizeFeasibilityPayload (SP skips BMI filter when both are 0)
    MinBMIValue: String(form.MinBMIValue || "").trim(),
    BMIValue: String(form.BMIValue || "").trim(),
    InclFilter: form.InclFilter || "All",
    ExclFilter: form.ExclFilter || "All",
    InclFiltermedi: form.InclFiltermedi || "All",
    ExclFiltermedi: form.ExclFiltermedi || "All",
    PostCode: postCodes.join(","),
    MedTerms: buildMedTerms(),
  });

  const validateForm = () => {
    const next = validateRanges(form);
    if (!String(form.ParticipantName || "").trim()) {
      next.ParticipantName = "Feasibility name is required.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handleSave = async (asNew = false) => {
    if (!validateForm()) return;
    // Legacy Save As: empty ParticipantId inserts a new query; original stays unchanged.
    const saveAsNew = Boolean(asNew);
    showLoader(
      saveAsNew
        ? "Saving as new feasibility query..."
        : isEdit
          ? "Updating feasibility query..."
          : "Saving feasibility query...",
    );
    try {
      const result = await saveFeasibility(
        userData,
        buildPayload(saveAsNew),
      );
      const newId = String(
        result?.ParticipantId || result?.participantId || "",
      ).trim();
      if (newId && !saveAsNew) setSavedParticipantId(newId);
      showAlert({
        message:
          result?.MSG ||
          result?.Msg ||
          result?.msg ||
          (saveAsNew
            ? "Feasibility query saved as new."
            : isEdit
              ? "Feasibility query updated."
              : "Feasibility query saved."),
        type: "success",
      });
      onSaved?.(newId || null);
      handleClose();
    } catch (err) {
      showAlert({
        message:
          err?.message ||
          (saveAsNew
            ? "Could not save feasibility as new."
            : "Could not save feasibility query."),
      });
    } finally {
      hideLoader();
    }
  };

  const handleRunQuery = async () => {
    const rangeErrors = validateRanges(form);
    if (Object.keys(rangeErrors).length) {
      setErrors(rangeErrors);
      return;
    }
    showLoader("Running feasibility query...");
    try {
      await runQueryWithPayload(
        {
          ...buildPayload(false),
          ParticipantId: savedParticipantId || "",
        },
        { keepPrevious: true, previousRows: volunteerRows },
      );
      window.requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    } catch (err) {
      showAlert({
        message: err?.message || "Could not run feasibility query.",
      });
      setQueryRan(true);
    } finally {
      hideLoader();
    }
  };

  const handleExport = () => {
    if (!volunteerRows.length) {
      showAlert({ message: "Run the query before exporting participants." });
      return;
    }
    exportVolunteersCsv(volunteerRows);
  };

  const matchCount = useMemo(
    () => (Array.isArray(volunteerRows) ? volunteerRows.length : 0),
    [volunteerRows],
  );

  const controlClass = (error) =>
    `form-control${error ? " is-invalid" : ""}`;

  return (
    <>
      <CtmsWorkspaceModal
        open={open}
        title={isEdit ? "Edit Feasibility" : "Create Feasibility"}
        titleId="pm-create-feasibility-title"
        onClose={handleClose}
        dialogClassName="ctms-workspace-modal__dialog--full"
        contentClassName="pm-add-modal pm-feas-modal"
      >
        <div className="pm-add-modal__form pm-feas-form">
          <div className="pm-feas-body pm-feas-body--columns">
            <section className="pm-add-card pm-feas-col">
              <div className="pm-add-card__head">
                <h2 className="pm-add-card__title">Query criteria</h2>
                <p className="pm-add-card__sub">Demographics & identity</p>
              </div>
              <div className="pm-feas-criteria">
                <Field
                  label="Feasibility name"
                  required
                  error={errors.ParticipantName}
                >
                  <input
                    className={controlClass(errors.ParticipantName)}
                    value={form.ParticipantName}
                    onChange={(e) =>
                      updateField("ParticipantName", e.target.value)
                    }
                    placeholder="e.g. Asthma cohort"
                    aria-invalid={Boolean(errors.ParticipantName)}
                  />
                </Field>
                <div className="pm-feas-criteria__row">
                  <Field label="Min age" error={errors.MinAgelimit}>
                    <input
                      className={controlClass(errors.MinAgelimit)}
                      inputMode="numeric"
                      maxLength={2}
                      value={form.MinAgelimit}
                      onChange={(e) =>
                        updateField(
                          "MinAgelimit",
                          e.target.value.replace(/\D/g, "").slice(0, 2),
                        )
                      }
                      placeholder="e.g. 18"
                    />
                  </Field>
                  <Field label="Max age" error={errors.MaxAgeLimit}>
                    <input
                      className={controlClass(errors.MaxAgeLimit)}
                      inputMode="numeric"
                      maxLength={3}
                      value={form.MaxAgeLimit}
                      onChange={(e) =>
                        updateField(
                          "MaxAgeLimit",
                          e.target.value.replace(/\D/g, "").slice(0, 3),
                        )
                      }
                      placeholder="e.g. 65"
                    />
                  </Field>
                </div>
                <div className="pm-feas-criteria__row">
                  <Field label="Min BMI" error={errors.MinBMIValue}>
                    <input
                      className={controlClass(errors.MinBMIValue)}
                      maxLength={6}
                      value={form.MinBMIValue}
                      onChange={(e) =>
                        updateField(
                          "MinBMIValue",
                          e.target.value.replace(/[^\d.]/g, "").slice(0, 6),
                        )
                      }
                      placeholder="e.g. 18.5"
                    />
                  </Field>
                  <Field label="Max BMI" error={errors.BMIValue}>
                    <input
                      className={controlClass(errors.BMIValue)}
                      maxLength={6}
                      value={form.BMIValue}
                      onChange={(e) =>
                        updateField(
                          "BMIValue",
                          e.target.value.replace(/[^\d.]/g, "").slice(0, 6),
                        )
                      }
                      placeholder="e.g. 30"
                    />
                  </Field>
                </div>
                <Field label="Gender">
                  <div className="pm-feas-gender">
                    {GENDER_CHOICES.map((gender) => {
                      const active = genders.includes(gender);
                      return (
                        <button
                          key={gender}
                          type="button"
                          className={`pm-feas-gender__btn${
                            active ? " is-active" : ""
                          }`}
                          onClick={() => toggleGender(gender)}
                        >
                          {gender}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Post code">
                  <BadgeMultiSelect
                    options={postCodeOptions}
                    value={postCodes}
                    onChange={setPostCodes}
                    emptyLabel="All post codes"
                    addLabel="+ Add"
                    showSearch
                    showSelectAll
                    selectAllLabel="Select all"
                    clearLabel="Clear"
                    compactSummary
                    popoverTitle="Post codes"
                    popoverSubtitle="Select one or more"
                  />
                </Field>
              </div>
            </section>

            <section className="pm-add-card pm-feas-col">
              <div className="pm-add-card__head">
                <h2 className="pm-add-card__title">Medical</h2>
                <p className="pm-add-card__sub">Include / exclude conditions</p>
              </div>
              <div className="pm-feas-stack">
                <div className="pm-feas-bucket">
                  <div className="pm-feas-bucket__bar">
                    <h3 className="pm-feas-bucket__title">Include</h3>
                    <ModeToggle
                      name="pm-feas-med-incl"
                      value={form.InclFilter}
                      onChange={(mode) => updateField("InclFilter", mode)}
                    />
                  </div>
                  <SnomedSearchField
                    userData={userData}
                    termType="All"
                    label=""
                    placeholder="Search condition…"
                    clearOnSelect
                    onSelect={(item) =>
                      addTerm(setMedInclude, item, "Medical History")
                    }
                  />
                  <TermChips
                    items={medInclude}
                    onRemove={(item) => removeTerm(setMedInclude, item)}
                    emptyLabel="No terms yet"
                  />
                </div>
                <div className="pm-feas-bucket">
                  <div className="pm-feas-bucket__bar">
                    <h3 className="pm-feas-bucket__title">Exclude</h3>
                    <ModeToggle
                      name="pm-feas-med-excl"
                      value={form.ExclFilter}
                      onChange={(mode) => updateField("ExclFilter", mode)}
                    />
                  </div>
                  <SnomedSearchField
                    userData={userData}
                    termType="All"
                    label=""
                    placeholder="Search condition…"
                    clearOnSelect
                    onSelect={(item) =>
                      addTerm(setMedExclude, item, "Medical History")
                    }
                  />
                  <TermChips
                    items={medExclude}
                    onRemove={(item) => removeTerm(setMedExclude, item)}
                    emptyLabel="No terms yet"
                  />
                </div>
              </div>
            </section>

            <section className="pm-add-card pm-feas-col">
              <div className="pm-add-card__head">
                <h2 className="pm-add-card__title">Medication</h2>
                <p className="pm-add-card__sub">Include / exclude medicines</p>
              </div>
              <div className="pm-feas-stack">
                <div className="pm-feas-bucket">
                  <div className="pm-feas-bucket__bar">
                    <h3 className="pm-feas-bucket__title">Include</h3>
                    <ModeToggle
                      name="pm-feas-medi-incl"
                      value={form.InclFiltermedi}
                      onChange={(mode) => updateField("InclFiltermedi", mode)}
                    />
                  </div>
                  <SnomedSearchField
                    userData={userData}
                    termType="Medication"
                    label=""
                    placeholder="Search medication…"
                    clearOnSelect
                    onSelect={(item) =>
                      addTerm(setMediInclude, item, "Medication")
                    }
                  />
                  <TermChips
                    items={mediInclude}
                    onRemove={(item) => removeTerm(setMediInclude, item)}
                    emptyLabel="No terms yet"
                  />
                </div>
                <div className="pm-feas-bucket">
                  <div className="pm-feas-bucket__bar">
                    <h3 className="pm-feas-bucket__title">Exclude</h3>
                    <ModeToggle
                      name="pm-feas-medi-excl"
                      value={form.ExclFiltermedi}
                      onChange={(mode) => updateField("ExclFiltermedi", mode)}
                    />
                  </div>
                  <SnomedSearchField
                    userData={userData}
                    termType="Medication"
                    label=""
                    placeholder="Search medication…"
                    clearOnSelect
                    onSelect={(item) =>
                      addTerm(setMediExclude, item, "Medication")
                    }
                  />
                  <TermChips
                    items={mediExclude}
                    onRemove={(item) => removeTerm(setMediExclude, item)}
                    emptyLabel="No terms yet"
                  />
                </div>
              </div>
            </section>

            <section
              ref={resultsRef}
              className={`pm-add-card pm-feas-col pm-feas-results-card${
                queryRan ? " is-visible" : ""
              }`}
            >
              <div className="pm-add-card__head pm-feas-results-head">
                <div>
                  <h2 className="pm-add-card__title">Participants</h2>
                  <p className="pm-add-card__sub">
                    {queryRan
                      ? matchCount
                        ? `${matchCount} matched`
                        : "No matches"
                      : "Run Query for results"}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleExport}
                  disabled={!matchCount}
                  title="Export participants"
                >
                  <i className="fas fa-file-csv me-1" aria-hidden="true" />
                  Export
                </button>
              </div>
              {filterNotice ? (
                <div className="pm-feas-filter-notice" role="status">
                  <i className="fas fa-info-circle" aria-hidden="true" />
                  <span>{filterNotice}</span>
                </div>
              ) : null}
              <div className="pm-feas-results">
                {!queryRan ? (
                  <div className="pm-feas-results__placeholder">
                    Click <strong>Run Query</strong> to list matching
                    participants here.
                  </div>
                ) : matchCount ? (
                  <div className="pm-feas-vol-list">
                    {volunteerRows.map((row, index) => (
                      <ParticipantResultCard
                        key={volunteerCardId(row, index)}
                        row={row}
                        onOpen={(item) => {
                          const volId = fieldOf(
                            item,
                            "VolunteerDemRecordId",
                            "VolunteerId",
                            "volunteerDemRecordId",
                          );
                          if (volId) setDetailVolunteerId(volId);
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="pm-feas-results__placeholder">
                    No matched participants.
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="pm-add-modal__footer">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleRunQuery}
            >
              <i className="fas fa-play me-1" aria-hidden="true" />
              Run Query
            </button>
            {isEdit ? (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handleSave(true)}
                title="Save a new copy; the original query stays unchanged"
              >
                <i className="fas fa-copy me-1" aria-hidden="true" />
                Save As
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleSave(false)}
            >
              <i className="fas fa-save me-1" aria-hidden="true" />
              {isEdit ? "Update Query" : "Save Query"}
            </button>
          </div>
        </div>
      </CtmsWorkspaceModal>

      <FeasibilityVolunteerDetailModal
        open={Boolean(detailVolunteerId)}
        volunteerId={detailVolunteerId}
        onClose={() => setDetailVolunteerId("")}
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}
